/**
 * VibesitesImage — CDN-optimized image component.
 * Ported from client/src/components/media/mh-image.tsx.
 *
 * Accepts either a `storageKey` (CDN-optimized with srcSet) or a plain `url`
 * (passthrough for external sources). Shows a placeholder on error / missing.
 */

import { useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import { getMediaUrl, getMediaSrcSet, parseStorageKey } from '../lib/media';

export interface VibesitesImageProps {
  storageKey?: string;
  url?: string;
  alt?: string;
  width?: number;
  height?: number;
  /** Absolute-position the image to fill its nearest positioned parent. */
  fill?: boolean;
  className?: string;
  style?: React.CSSProperties;
  sizes?: string;
  quality?: number;
  priority?: boolean;
  objectFit?: 'contain' | 'cover';
  onLoad?: () => void;
  onError?: () => void;
  draggable?: boolean;
}

export function VibesitesImage({
  storageKey,
  url,
  alt = '',
  width,
  height,
  fill,
  className,
  style,
  sizes = '100vw',
  quality,
  priority = false,
  objectFit,
  onLoad,
  onError,
  draggable,
}: VibesitesImageProps) {
  const [_loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const resolvedUrl = url || '';

  // If url is already a CDN URL, extract the storage key so we can
  // re-optimize with the correct width/quality instead of passing through
  // whatever preset was baked into the original URL.
  const resolvedKey = storageKey || parseStorageKey(resolvedUrl);

  const srcSet = resolvedKey ? getMediaSrcSet(resolvedKey, quality) : undefined;
  const src = resolvedKey ? getMediaUrl(resolvedKey, { width: width || 640, quality }) : resolvedUrl;

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  if (error || !src) {
    return (
      <div
        className={cn('flex items-center justify-center bg-gray-200', fill && 'absolute inset-0', className)}
        style={!fill ? { width, height, ...style } : style}
      >
        <svg className="h-8 w-8 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      </div>
    );
  }

  // When fill is used, apply layout classes. Otherwise render a plain <img>
  // so it inherits sizing from the theme's own CSS without interference.
  const fillClasses = fill
    ? cn('absolute inset-0 h-full w-full', objectFit === 'contain' ? 'object-contain' : 'object-cover')
    : objectFit ? (objectFit === 'contain' ? 'object-contain' : 'object-cover') : undefined;

  return (
    <img
      src={src}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      loading={priority ? 'eager' : 'lazy'}
      // React 18.2 doesn't recognize the camelCase `fetchPriority` prop and
      // emits a warning ("React does not recognize the `fetchPriority` prop on
      // a DOM element"). The DOM attribute is `fetchpriority` either way, so
      // we spread it under the lowercase name to bypass the warning.
      {...({ fetchpriority: priority ? 'high' : 'auto' } as { fetchpriority: string })}
      draggable={draggable}
      style={style}
      onLoad={() => {
        setLoaded(true);
        onLoad?.();
      }}
      onError={() => {
        setError(true);
        onError?.();
      }}
      className={cn(fillClasses, className)}
    />
  );
}
